import { Alert, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import axios from 'axios';
import { useCallback, useState } from 'react';
import { ConfirmDialog } from '../../../components/Dialog/ConfirmDialog/ConfirmDialog';
import {
  RebuildScope,
  RoundCreationSettingsUpdate,
  RoundRebuildRequest,
  RoundRebuildResponse,
} from '../../../data-models/round-creation-settings.data-model';
import { getErrorMessage } from '../../../services/queryHelpers';
import { getRoundLabel } from '../../../util/preference-utils';
import { scopeCopy } from './roundCreationCopy';

const SCOPE_ORDER: RebuildScope[] = [RebuildScope.GO_FORWARD, RebuildScope.UNEDITED_SYSTEM, RebuildScope.ALL];

interface RebuildRoundsDialogProps {
  open: boolean;
  onClose: () => void;
  settings: RoundCreationSettingsUpdate;
  rebuild: (payload: RoundRebuildRequest) => Promise<RoundRebuildResponse>;
  isRebuilding: boolean;
  onApplied: (response: RoundRebuildResponse) => void;
  /** Reaching back over existing data is customer success's call; everyone else saves go-forward. */
  allowRetroactive?: boolean;
}

/** The API's 409 carries the live count both as a field and in its message; take either. */
export function handEditedCountFrom(err: unknown): number | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) {
    return null;
  }
  const data = err.response.data as { expected?: unknown; message?: unknown } | undefined;
  if (typeof data?.expected === 'number') {
    return data.expected;
  }
  const match = typeof data?.message === 'string' ? /confirmHandEditedWipe=(\d+)/.exec(data.message) : null;
  return match ? Number(match[1]) : null;
}

export function summarize(response: RoundRebuildResponse) {
  const sum = (pick: (c: RoundRebuildResponse['companies'][number]) => number) =>
    response.companies.reduce((acc, c) => acc + pick(c), 0);
  return {
    companies: response.companies.length,
    roundsWiped: sum((c) => c.roundsWiped),
    created: sum((c) => c.created),
    assigned: sum((c) => c.assigned),
    pendingAssignment: sum((c) => c.pendingAssignment),
    unassigned: sum((c) => c.unassigned),
    documentsReattached: sum((c) => c.documentsReattached),
    documentsNeedingReview: sum((c) => c.documentsNeedingReview),
    roundsWithoutReplacement: sum((c) => c.roundsWithoutReplacement),
    failedCompanies: response.companies.filter((c) => c.errors.length > 0).length,
  };
}

export function RebuildRoundsDialog({
  open,
  onClose,
  settings,
  rebuild,
  isRebuilding,
  onApplied,
  allowRetroactive = true,
}: RebuildRoundsDialogProps) {
  const roundLabel = getRoundLabel();
  const lower = roundLabel.toLowerCase();
  const copy = scopeCopy(roundLabel);
  const scopes = allowRetroactive ? SCOPE_ORDER : [RebuildScope.GO_FORWARD];
  const [scope, setScope] = useState<RebuildScope>(RebuildScope.GO_FORWARD);
  // The count the API named on its 409; resent as agreement. Never remembered across opens.
  const [handEditedToWipe, setHandEditedToWipe] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<RoundRebuildResponse | null>(null);

  const close = useCallback(() => {
    setScope(RebuildScope.GO_FORWARD);
    setHandEditedToWipe(null);
    setError(null);
    setReport(null);
    onClose();
  }, [onClose]);

  const onScopeChange = (next: RebuildScope) => {
    setScope(next);
    setHandEditedToWipe(null);
    setError(null);
  };

  const onConfirm = useCallback(async () => {
    setError(null);
    let confirmed = handEditedToWipe;
    // The API demands the confirmation whenever it is absent, including when the count is zero.
    // Agreeing to destroy nothing is not a decision, so it is resent once rather than asked about;
    // the loop can only run twice, because the retry sends a confirmation of its own.
    for (;;) {
      try {
        const response = await rebuild({
          scope,
          settings,
          confirmHandEditedWipe: scope === RebuildScope.ALL && confirmed !== null ? confirmed : undefined,
        });
        setReport(response);
        onApplied(response);
        return;
      } catch (err) {
        const count = handEditedCountFrom(err);
        if (count === 0 && confirmed === null) {
          confirmed = 0;
          continue;
        }
        if (count !== null) {
          setHandEditedToWipe(count);
          return;
        }
        setError(getErrorMessage(err, `The ${roundLabel.toLowerCase()}s could not be rebuilt`));
        return;
      }
    }
  }, [rebuild, scope, settings, handEditedToWipe, onApplied, roundLabel]);

  const needsWipeConfirmation = scope === RebuildScope.ALL && handEditedToWipe !== null;
  const confirmButtonText = report
    ? 'Close'
    : needsWipeConfirmation
      ? `Destroy ${handEditedToWipe} ${handEditedToWipe === 1 ? lower : `${lower}s`} and rebuild`
      : scope === RebuildScope.GO_FORWARD
        ? 'Save'
        : 'Save and rebuild';

  return (
    <ConfirmDialog
      open={open}
      onClose={close}
      onConfirm={report ? close : onConfirm}
      loading={isRebuilding}
      title={`Save and apply ${lower} settings`}
      footerButtonsProps={{
        confirmButtonText,
        cancelButtonText: report ? undefined : 'Cancel',
        onCancelDisabled: isRebuilding,
      }}
    >
      {report ? (
        <RebuildReport response={report} roundLabel={roundLabel} />
      ) : (
        <Stack gap='1rem'>
          <Typography variant='body2' color='text.secondary'>
            The settings are saved first. Then choose how far back the new rules reach.
          </Typography>
          {!allowRetroactive && (
            <Typography variant='caption' color='text.secondary'>
              {`Rebuilding ${lower}s that already exist is a customer success action; ask them if this change needs to reach back.`}
            </Typography>
          )}
          <RadioGroup value={scope} onChange={(e) => onScopeChange(e.target.value as RebuildScope)}>
            {scopes.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                disabled={isRebuilding}
                sx={{ alignItems: 'flex-start', mb: '0.5rem' }}
                label={
                  <Stack>
                    <Typography variant='body2'>{copy[option].label}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {copy[option].description}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </RadioGroup>
          {needsWipeConfirmation && (
            <Alert severity='warning'>
              {`This destroys ${handEditedToWipe} ${handEditedToWipe === 1 ? lower : `${lower}s`} that "rebuild ${lower}s nobody has edited" would have kept — hand-fixed ones, and ones with no recorded history, which cannot be told apart. They are rebuilt from the ledger and their history notes are carried across. Confirm to proceed.`}
            </Alert>
          )}
          {error && <Alert severity='error'>{error}</Alert>}
        </Stack>
      )}
    </ConfirmDialog>
  );
}

function RebuildReport({ response, roundLabel }: { response: RoundRebuildResponse; roundLabel: string }) {
  const s = summarize(response);
  const lower = roundLabel.toLowerCase();
  const rows: [string, number][] = [
    ['Companies processed', s.companies],
    [`${roundLabel}s wiped`, s.roundsWiped],
    [`${roundLabel}s created`, s.created],
    ['Transactions assigned', s.assigned],
    ['Transactions waiting (grace period)', s.pendingAssignment],
    ['Transactions unassigned', s.unassigned],
    ['Documents re-attached', s.documentsReattached],
    ['Documents needing review', s.documentsNeedingReview],
    [`${roundLabel}s left without a replacement`, s.roundsWithoutReplacement],
  ];
  return (
    <Stack gap='0.75rem'>
      <Typography variant='body2'>
        {`Saved as version ${response.settingsVersion}${response.auditId != null ? ` (audit #${response.auditId})` : ''}.`}
      </Typography>
      <Stack component='dl' gap='0.25rem' sx={{ m: 0 }}>
        {rows.map(([label, value]) => (
          <Stack key={label} direction='row' justifyContent='space-between'>
            <Typography component='dt' variant='body2' color='text.secondary'>
              {label}
            </Typography>
            <Typography component='dd' variant='body2' sx={{ m: 0 }}>
              {value}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {s.failedCompanies > 0 && (
        <Alert severity='error'>
          {`${s.failedCompanies} ${s.failedCompanies === 1 ? 'company was' : 'companies were'} not rebuilt; their ${lower}s are as they were. Details are on the audit record.`}
        </Alert>
      )}
    </Stack>
  );
}
